// Relative luminance and contrast ratio per WCAG 2.1. The Buffalo panels are drawn on
// a hardcoded black background regardless of the app's theme mode, so accent colors
// picked for a light theme can land on black and need checking before they're used.

const parseHex = (color: string): number[] => {
    const hex = color.replace("#", "")
    return [0, 2, 4].map(offset => parseInt(hex.slice(offset, offset + 2), 16))
}

const toHex = (channels: number[]): string =>
    `#${channels.map(c => c.toString(16).padStart(2, "0")).join("")}`

const relativeLuminance = ([r, g, b]: number[]) => {
    const channel = (value: number) => {
        const c = value / 255
        return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
    }
    return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b)
}

export const contrastRatio = (color1: string, color2: string): number => {
    const luminance1 = relativeLuminance(parseHex(color1))
    const luminance2 = relativeLuminance(parseHex(color2))
    const lighter = Math.max(luminance1, luminance2)
    const darker = Math.min(luminance1, luminance2)

    return (lighter + 0.05) / (darker + 0.05)
}

// Contrast ratio a color has to clear before it's used as text.
const TEXT_CONTRAST_TARGET = 7

// The darker floatability colors (the deep violet of Very Low, the saturated red of
// Flood) are hard to read as text on the app's black panels. White is mixed into them
// until they clear TEXT_CONTRAST_TARGET, which lifts them off the background while
// keeping the hue that ties the label to the map. Colors that already pass come back
// untouched, so only the ones that need it change.
export const toLegibleOnBlack = (color: string): string => {
    const rgb = parseHex(color)

    for (let whiteAmount = 0; whiteAmount <= 1; whiteAmount += 0.02) {
        const mixed = rgb.map(c => Math.round(c + (255 - c) * whiteAmount))
        if (contrastRatio(toHex(mixed), "#000000") >= TEXT_CONTRAST_TARGET)
            return toHex(mixed)
    }

    return "#ffffff"
}

// Picks black or white for text sitting on top of a filled background, whichever
// reads better. The theme's accent is bright pink in dark mode but a much darker
// magenta in light mode, so a hardcoded label color fails on one of the two.
export const getContrastingTextColor = (backgroundColor: string): string =>
    contrastRatio("#000000", backgroundColor) >= contrastRatio("#ffffff", backgroundColor)
        ? "black"
        : "white"
